import { useEffect, useRef, useState } from 'react';
import { request } from '../lib/api';
import { authError, type CustomerLoginResult } from '../lib/authSecurity';

type GoogleResult = CustomerLoginResult | { status: 'link_required' | 'email_confirmation_required'; message?: string; email?: string } | { ok: true };
type GoogleIdentity = { initialize: (options: { client_id: string; nonce: string; callback: (response: { credential: string }) => void; auto_select: boolean }) => void; renderButton: (element: HTMLElement, options: { theme: string; size: string; text: string }) => void };
declare global { interface Window { google?: { accounts: { id: GoogleIdentity } } } }
let googleScript: Promise<void> | undefined;
function loadGoogle() {
  if (window.google?.accounts.id) return Promise.resolve();
  if (!googleScript) googleScript = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client'; script.async = true;
    script.onload = () => resolve(); script.onerror = () => { googleScript = undefined; script.remove(); reject(new Error('Google could not load')); };
    document.head.appendChild(script);
  });
  return googleScript;
}
export function GoogleSignIn({ onResult, intent = 'login', rememberMe = false, createIfMissing = false, getStepUpToken, disabled = false }: {
  onResult: (result: GoogleResult) => void; intent?: 'login' | 'link'; rememberMe?: boolean; createIfMissing?: boolean; getStepUpToken?: () => Promise<string | undefined>; disabled?: boolean;
}) {
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const button = useRef<HTMLDivElement>(null);
  const active = useRef(true);
  const optionsRef = useRef({ onResult, rememberMe, createIfMissing, getStepUpToken });
  optionsRef.current = { onResult, rememberMe, createIfMissing, getStepUpToken };
  useEffect(() => {
    active.current = true;
    request<{ enabled: boolean }>('/auth/google/config').then(config => { if (active.current) setEnabled(config.enabled); }).catch(() => { if (active.current) setEnabled(false); });
    return () => { active.current = false; };
  }, []);
  const begin = async () => {
    if (disabled || pending) return;
    setPending(true); setError(null);
    try {
      const stepUpToken = await optionsRef.current.getStepUpToken?.();
      const challenge = await request<{ challengeId: string; nonce: string; clientId: string }>('/auth/google/options', { method: 'POST', body: JSON.stringify({ intent, stepUpToken, rememberMe: optionsRef.current.rememberMe, createIfMissing: optionsRef.current.createIfMissing }) });
      await loadGoogle();
      if (!active.current || !button.current) return;
      window.google!.accounts.id.initialize({ client_id: challenge.clientId, nonce: challenge.nonce, auto_select: false, callback: async ({ credential }) => {
        if (!active.current) return;
        setPending(true);
        try {
          const result = await request<GoogleResult>('/auth/google/verify', { method: 'POST', body: JSON.stringify({ challengeId: challenge.challengeId, credential }) });
          if (active.current) optionsRef.current.onResult(result);
        } catch (err) { if (active.current) setError(authError(err)); }
        finally { if (active.current) { setPending(false); button.current?.replaceChildren(); } }
      } });
      button.current.replaceChildren();
      window.google!.accounts.id.renderButton(button.current, { theme: 'outline', size: 'large', text: intent === 'link' ? 'continue_with' : 'signin_with' });
    } catch (err) { setError(authError(err)); }
    finally { if (active.current) setPending(false); }
  };
  return <div className="form-stack">
    {enabled === false ? <p className="helper-text">Google sign-in is unavailable. You can use email or a registered passkey.</p> : null}
    {enabled === null ? <p className="helper-text">Checking Google sign-in availability...</p> : null}
    {enabled ? <button className="button-secondary" type="button" disabled={pending || disabled} onClick={begin}>{pending ? 'Preparing Google...' : intent === 'link' ? 'Link Google account' : 'Continue with Google'}</button> : null}
    <div ref={button} />
    {error ? <div className="alert" role="alert">{error}</div> : null}
  </div>;
}
